import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE, absUrl } from "./siteMeta.js";

/**
 * Per-page <head>. Renders title, description, canonical, Open Graph, Twitter
 * card, and any JSON-LD blocks.
 *
 * Every crawlable page must render exactly one of these. The app previously had
 * a single global <title> in public/index.html and nothing else, so every URL
 * looked identical to a crawler.
 *
 * `canonical` is always absolute and always self-referencing unless overridden -
 * a relative canonical is ignored by Google, and a missing one lets query-string
 * variants (?utm_source=...) get indexed as duplicates.
 */
const SeoHead = ({
  title,
  description,
  path = "/",
  keywords = [],
  image = SITE.ogImage,
  type = "website",
  noindex = false,
  schemas = [],
  publishedAt,
  updatedAt,
  // Language of this page ("en" | "es"). Sets <html lang> so screen readers and
  // search engines read a Spanish page as Spanish.
  lang = "en",
  // Other-language versions of THIS page: [{ hreflang: "es", path: "/es/..." }].
  // Rendered as rel="alternate" hreflang links, plus a self-reference, which is
  // what Google requires for hreflang to count. Pairs must link both ways.
  alternates = [],
}) => {
  // absUrl() guarantees the trailing-slash form the server actually serves 200
  // for, so the canonical is self-referencing rather than pointing at a 301.
  const canonical = absUrl(path);

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      <link rel="canonical" href={canonical} />
      {alternates.length > 0 && <link rel="alternate" hrefLang={lang} href={canonical} />}
      {alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={absUrl(a.path)} />
      ))}

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={lang === "es" ? "es_US" : "en_US"} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {type === "article" && publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}
      {type === "article" && (updatedAt || publishedAt) && (
        <meta property="article:modified_time" content={updatedAt || publishedAt} />
      )}

      {schemas.map((s, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
